import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Target, CheckCircle, Clock } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface Challenge {
  id: number
  challenge_id: number
  title: string
  description: string
  type: string
  xp_reward: number
  progress: number
  progress_data: any
  status: string
  deadline: string | null
  started_at: string | null
  completed_at: string | null
}

interface ChallengesData {
  active: Challenge[]
  completed: Challenge[]
}

export default function StudentChallenges() {
  const { data, isLoading } = useQuery<ChallengesData>({
    queryKey: ['student-challenges'],
    queryFn: async () => {
      const response = await api.get('/api/student/challenges')
      return response.data
    },
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-surface-card rounded"></div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-surface-card rounded-xl"></div>
          ))}
        </div>
      </div>
    )
  }

  const typeColors: Record<string, string> = {
    daily: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    weekly: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    skill_recovery: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    family: 'bg-green-500/20 text-green-400 border-green-500/30',
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Target className="w-8 h-8 text-quest-primary" />
          Challenges
        </h1>
        <p className="text-gray-400 mt-1">
          Complete challenges to earn XP and level up
        </p>
      </div>

      {/* Active Challenges */}
      <section>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-quest-accent" />
          Active Challenges
        </h2>

        {data?.active.length === 0 ? (
          <div className="card text-center py-8 text-gray-400">
            <p>No active challenges</p>
            <p className="text-sm mt-1">New challenges will appear here</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {data?.active.map((challenge) => (
              <div
                key={challenge.id}
                className={`card border ${typeColors[challenge.type] || 'border-gray-700/50'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${typeColors[challenge.type] || 'bg-gray-700'}`}>
                        {challenge.type.replace('_', ' ')}
                      </span>
                      <span className="text-sm font-medium text-quest-accent">
                        +{challenge.xp_reward} XP
                      </span>
                    </div>
                    <h3 className="text-lg font-medium">{challenge.title}</h3>
                    {challenge.description && (
                      <p className="text-gray-400 mt-1">{challenge.description}</p>
                    )}
                  </div>
                </div>

                {/* Progress */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Progress</span>
                    <span className="font-medium">{Math.round(challenge.progress)}%</span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-quest-primary to-quest-secondary transition-all"
                      style={{ width: `${challenge.progress}%` }}
                    />
                  </div>
                </div>

                {/* Deadline */}
                {challenge.deadline && (
                  <div className="mt-3 flex items-center gap-1 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>
                      Due {formatDistanceToNow(new Date(challenge.deadline), { addSuffix: true })}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Completed Challenges */}
      <section>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          Completed
        </h2>

        {data?.completed.length === 0 ? (
          <div className="card text-center py-8 text-gray-400">
            <p>No completed challenges yet</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {data?.completed.map((challenge) => (
              <div
                key={challenge.id}
                className="card bg-green-900/10 border border-green-900/30"
              >
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-medium">{challenge.title}</h3>
                    {challenge.completed_at && (
                      <p className="text-sm text-gray-500">
                        Completed {formatDistanceToNow(new Date(challenge.completed_at), { addSuffix: true })}
                      </p>
                    )}
                  </div>
                  <span className="text-quest-accent font-medium">
                    +{challenge.xp_reward} XP
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
